import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

export interface ConfirmOptions {
  title?: string;
  /** Body text. Multi-line strings are rendered with line breaks preserved. */
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm button as destructive (red). */
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | undefined>(undefined);

interface PendingState {
  opts: ConfirmOptions;
  resolve: (result: boolean) => void;
}

/**
 * App-wide confirmation dialog. Replaces native window.confirm with a styled modal that
 * matches the rest of the app. Usage:
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ message: 'Delete this?', danger: true })) { ... }
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingState | null>(null);

  const confirm = useCallback<ConfirmFn>(
    opts => new Promise<boolean>(resolve => setPending({ opts, resolve })),
    [],
  );

  const settle = useCallback(
    (result: boolean) => {
      setPending(prev => {
        prev?.resolve(result);
        return null;
      });
    },
    [],
  );

  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') settle(false);
      else if (e.key === 'Enter') settle(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending, settle]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending ? (
        <div className="modal-backdrop" onClick={() => settle(false)}>
          <div
            className="modal"
            style={{ maxWidth: 460 }}
            role="alertdialog"
            aria-modal="true"
            onClick={e => e.stopPropagation()}
          >
            {pending.opts.title ? <h3 style={{ marginTop: 0 }}>{pending.opts.title}</h3> : null}
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{pending.opts.message}</div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={() => settle(false)}>
                {pending.opts.cancelLabel ?? 'Cancel'}
              </button>
              <button
                type="button"
                className={`btn ${pending.opts.danger ? 'btn-danger' : 'btn-primary'}`}
                onClick={() => settle(true)}
                autoFocus
              >
                {pending.opts.confirmLabel ?? 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}
